import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { t } from 'utils/i18n';
import type { ReferencePhoto } from '../references/references-api';
import { stripPhotoKeys, toStripState } from './reference-steps';
import {
    RunBody, RunRequest, runSteps, StepsScope, StepsState, stopSteps, updateTaskStepSettings,
} from './steps-api';
import { useStepsPolling } from './use-steps-polling';

export type StepsNoun = 'drawing' | 'photo';

export interface StepsContextValue {
    taskId: number;
    projectId: number | null;
    scope: StepsScope;
    // what the run targets are called in the panel's copy
    noun: StepsNoun;
    currentFrame: number;
    state: StepsState | null;
    error: string | null;
    notice: string | null;
    busy: boolean;
    resultsPending: boolean;
    reloading: boolean;
    selected: number[];
    toggleSelected(frame: number): void;
    clearSelection(): void;
    run(request: RunRequest): Promise<void>;
    stop(): Promise<void>;
    setFunction(stepId: string, functionId: string): Promise<void>;
    setEnabled(stepId: string, enabled: boolean): Promise<void>;
    setLibraries(libraries: string[]): Promise<void>;
    loadResults(): Promise<void>;
    saveChanges(): Promise<void>;
    refresh(): void;
}

export const StepsContext = createContext<StepsContextValue | null>(null);

export function useStepsContext(): StepsContextValue | null {
    return useContext(StepsContext);
}

export interface StepsProviderProps {
    taskId: number;
    projectId: number | null;
    // references: frames are positions in the references strip (``photos``)
    scope?: StepsScope;
    photos?: ReferencePhoto[];
    currentFrame: number;
    hasUnsavedChanges(): boolean;
    save(): Promise<void>;
    reloadAnnotations(): Promise<void> | void;
    children: React.ReactNode;
}

export const alreadyDone = (noun: StepsNoun): string => (noun === 'photo' ?
    t('Already done on these photos. Use Run again to replace the results.') :
    t('Already done on these drawings. Use Run again to replace the results.'));
export const ALREADY_DONE = alreadyDone('drawing');
const NO_PHOTOS: ReferencePhoto[] = [];

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

export function StepsProvider(props: StepsProviderProps): JSX.Element {
    const {
        taskId, projectId, scope = 'drawings', photos = NO_PHOTOS, currentFrame, hasUnsavedChanges, save,
        reloadAnnotations, children,
    } = props;
    const noun: StepsNoun = scope === 'references' ? 'photo' : 'drawing';
    const {
        state: serverState, error: pollError, absent, refresh, replace,
    } = useStepsPolling(taskId, scope);
    const state = useMemo(
        () => (serverState && scope === 'references' ? toStripState(serverState, photos) : serverState),
        [serverState, scope, photos],
    );
    const [actionError, setActionError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState<number[]>([]);
    const [resultsPending, setResultsPending] = useState(false);
    const [reloading, setReloading] = useState(false);
    const seenVersion = useRef<string | null | undefined>(undefined);
    const lastCurrentStatus = useRef<string | undefined>(undefined);
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    // cvat-core swaps the whole collection when the reload returns: edits made meanwhile would
    // vanish, so the workspace is blocked (like while saving) until it is back
    const reload = useCallback(async (): Promise<void> => {
        setReloading(true);
        try {
            await reloadAnnotations();
        } finally {
            if (mounted.current) setReloading(false);
        }
    }, [reloadAnnotations]);

    // the effect below must see the latest callbacks without re-running on their identity
    const io = useRef({ hasUnsavedChanges, reload, currentFrame });
    io.current = { hasUnsavedChanges, reload, currentFrame };

    useEffect(() => {
        seenVersion.current = undefined;
        lastCurrentStatus.current = undefined;
        pending.current = false;
        setResultsPending(false);
        setSelected([]);
        setNotice(null);
    }, [taskId, scope]);

    useEffect(() => {
        if (!state) return;
        const currentStatus = state.frame_status[String(io.current.currentFrame)];
        const currentChanged = currentStatus !== lastCurrentStatus.current;
        lastCurrentStatus.current = currentStatus;
        if (seenVersion.current === undefined) {
            seenVersion.current = state.results_version;
            return;
        }
        if (state.results_version !== seenVersion.current) {
            seenVersion.current = state.results_version;
            pending.current = true;
        }
        if (!pending.current) return;
        // reloading the whole job after every drawing is heavy: wait for this drawing or the end
        if (state.running && !currentChanged) return;
        if (io.current.hasUnsavedChanges()) {
            setResultsPending(true);
            return;
        }
        pending.current = false;
        setResultsPending(false);
        io.current.reload().catch((e) => {
            if (mounted.current) setActionError(messageOf(e));
        });
    }, [state]);

    const withBusy = useCallback(async (action: () => Promise<void>): Promise<void> => {
        setBusy(true);
        setActionError(null);
        setNotice(null);
        try {
            await action();
        } catch (e) {
            setActionError(messageOf(e));
        } finally {
            setBusy(false);
        }
    }, []);

    const run = useCallback((request: RunRequest) => withBusy(async () => {
        const body: RunBody = { steps: request.steps, force: request.force };
        if (request.frames !== null) {
            if (scope === 'references') body.photos = stripPhotoKeys(request.frames, photos);
            else body.frames = request.frames;
        }
        if (hasUnsavedChanges()) await save();
        const { state: next, queued } = await runSteps(taskId, body, scope);
        replace(next);
        // the per-row Run picks its action from all drawings: the chosen ones may all be done
        if (!queued) setNotice(alreadyDone(noun));
    }), [taskId, scope, photos, noun, hasUnsavedChanges, save, replace, withBusy]);

    const stop = useCallback(() => withBusy(async () => {
        replace(await stopSteps(taskId, scope));
    }), [taskId, scope, replace, withBusy]);

    const setFunction = useCallback((stepId: string, functionId: string) => withBusy(async () => {
        replace(await updateTaskStepSettings(taskId, { functions: { [stepId]: functionId } }, scope));
    }), [taskId, scope, replace, withBusy]);

    const setEnabled = useCallback((stepId: string, enabled: boolean) => withBusy(async () => {
        replace(await updateTaskStepSettings(taskId, { enabled: { [stepId]: enabled } }, scope));
    }), [taskId, scope, replace, withBusy]);

    const setLibraries = useCallback((libraries: string[]) => withBusy(async () => {
        replace(await updateTaskStepSettings(taskId, { libraries }, scope));
    }), [taskId, scope, replace, withBusy]);

    const loadResults = useCallback(() => withBusy(async () => {
        await save();
        pending.current = false;
        setResultsPending(false);
        await reload();
    }), [save, reload, withBusy]);

    const saveChanges = useCallback(async () => {
        if (hasUnsavedChanges()) await save();
    }, [hasUnsavedChanges, save]);

    const toggleSelected = useCallback((frame: number) => {
        setSelected((prev) => (prev.includes(frame) ? prev.filter((f) => f !== frame) : [...prev, frame]));
    }, []);
    const clearSelection = useCallback(() => setSelected([]), []);

    const value = useMemo<StepsContextValue>(() => ({
        taskId,
        projectId,
        scope,
        noun,
        currentFrame,
        state,
        error: actionError ?? pollError,
        notice,
        busy,
        resultsPending,
        reloading,
        selected,
        toggleSelected,
        clearSelection,
        run,
        stop,
        setFunction,
        setEnabled,
        setLibraries,
        loadResults,
        saveChanges,
        refresh,
    }), [taskId, projectId, scope, noun, currentFrame, state, actionError, pollError, notice, busy, resultsPending,
        reloading, selected, toggleSelected, clearSelection, run, stop, setFunction, setEnabled, setLibraries,
        loadResults, saveChanges, refresh]);

    // the same tree either way, so the workspace below is not remounted
    return <StepsContext.Provider value={absent ? null : value}>{children}</StepsContext.Provider>;
}
